import React, { useEffect, useMemo, useState } from "react";
import { searchClients } from "../../../utils/api";

function normalizeQuery(q) {
  return String(q || "").trim();
}

function parseCode1088_1(input) {
  const raw = normalizeQuery(input);
  if (!raw) return null;

  // formato: "1088 1" (uno o más espacios)
  const m = raw.match(/^(\d+)\s+(\d+)$/);
  if (!m) return null;

  const clientNumber = Number(m[1]);
  const branchNo = Number(m[2]);
  if (!Number.isInteger(clientNumber) || clientNumber <= 0) return null;
  if (!Number.isInteger(branchNo) || branchNo <= 0) return null;

  return { client_number: clientNumber, branch_no: branchNo };
}

export default function ClientSuperSearch({ onEditClient }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const parsed = useMemo(() => parseCode1088_1(q), [q]);

  useEffect(() => {
    const query = normalizeQuery(q);

    // no dispares query si está vacío
    if (!query) {
      setRows([]);
      setErr(null);
      return;
    }

    const t = setTimeout(async () => {
      try {
        setLoading(true);
        setErr(null);
        const data = await searchClients(query);
        setRows(data || []);
      } catch (e) {
        setErr(e?.message || "Error buscando clientes");
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="rounded-xl border border-white/10 bg-card p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Super buscador de clientes</h2>
          <p className="text-sm text-muted-foreground">
            Buscá por código <b>1088 1</b>, número (<b>1088</b>), CUIT, razón social o dirección.
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          {loading ? "Buscando..." : parsed ? `Detectado: ${parsed.client_number} / suc ${parsed.branch_no}` : ""}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-400"
          placeholder='Ej: "1088 1" o "1088" o "3050..." o "ARCOR"'
        />
        <button
          type="button"
          onClick={() => setQ("")}
          className="px-3 py-2 rounded-md border border-white/15 bg-white/5 text-sm hover:bg-white/10"
        >
          Limpiar
        </button>
      </div>

      {err && (
        <div className="mt-3 text-sm text-red-300 bg-red-900/30 border border-red-600/50 rounded-md px-3 py-2">
          {err}
        </div>
      )}

      <div className="mt-4 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr className="border-b border-white/10">
              <th className="text-left py-2 pr-2">Cliente</th>
              <th className="text-left py-2 pr-2">Sucursal</th>
              <th className="text-left py-2 pr-2">Razón social</th>
              <th className="text-left py-2 pr-2">CUIT</th>
              <th className="text-left py-2 pr-2">Dirección</th>
              <th className="text-right py-2 pl-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="py-3 text-muted-foreground" colSpan={6}>
                  {normalizeQuery(q) ? (loading ? "Buscando..." : "Sin resultados") : "Escribí para buscar"}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="py-2 pr-2">
                    <span className="font-medium">{r.client_number ?? "—"}</span>
                  </td>
                  <td className="py-2 pr-2">{r.branch_no ?? "—"}</td>
                  <td className="py-2 pr-2">{r.name ?? "—"}</td>
                  <td className="py-2 pr-2">{r.cuit ?? r.tax_id ?? "—"}</td>
                  <td className="py-2 pr-2">{r.address ?? "—"}</td>
                  <td className="py-2 pl-2 text-right">
                    <button
                      type="button"
                      onClick={() => onEditClient?.(r)}
                      className="px-3 py-1.5 rounded-md border border-white/15 bg-white/5 hover:bg-white/10"
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {rows.length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            Resultados: {rows.length}
          </div>
        )}
      </div>
    </div>
  );
}
